// Product analytics via PostHog (https://posthog.com).
// Off until POSTHOG_KEY is set. Never sends from localhost / file:// unless the URL has ?analytics=on.
// Privacy: no cookies (localStorage), no session recording, no click autocapture, Do-Not-Track respected.
// Errors (message and stack trace) go to PostHog Error Tracking.

const POSTHOG_KEY = 'phc_tuh2tgN3ASsXrZFrmqPqZsZo5tGPif2mzg2kMkHEsY3X';   // PostHog project "Lofi Cities" (public, write-only)
const POSTHOG_HOST = 'https://us.i.posthog.com';     // EU projects: 'https://eu.i.posthog.com'

const local = location.protocol === 'file:' || /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
const enabled = Boolean(POSTHOG_KEY) && (!local || new URLSearchParams(location.search).get('analytics') === 'on');

let started = false;
// PostHog swaps window.posthog (a queueing stub) for the real instance once its script loads,
// so always look it up at call time instead of keeping a reference.
const ph = () => (started ? window.posthog : null);

export function initAnalytics(superProps = {}, { pageviews = true } = {}) {
  if (!enabled || started) return;
  started = true;
  // Official async loader: queue calls on a stub until the library arrives from PostHog's CDN.
  const stub = window.posthog = window.posthog || [];
  if (!stub.__SV) {
    stub._i = [];
    stub.init = (key, config, name) => stub._i.push([key, config, name]);
    for (const m of ['capture', 'captureException', 'register', 'register_once', 'unregister', 'identify', 'reset', 'opt_in_capturing', 'opt_out_capturing'])
      stub[m] = (...args) => stub.push([m, ...args]);
    stub.__SV = 1;
    const s = document.createElement('script');
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.src = POSTHOG_HOST.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
    document.head.append(s);
  }
  stub.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',              // anonymous events only, no person profiles
    persistence: 'localStorage',                     // no cookies
    autocapture: false,
    capture_exceptions: true,                        // uncaught errors and promise rejections; 10 of a kind, then 1 per 10 s
    disable_session_recording: true,
    capture_pageview: pageviews,
    capture_pageleave: pageviews,
    respect_dnt: true,
  });
  stub.register({ site: 'lofi-cities', app_version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'development', ...superProps });
}

/** Record an event. Safe to call anytime; a no-op when analytics is off or blocked. */
export function track(event, props = {}) {
  ph()?.capture(event, props);
}

/** Send an error the app caught itself to Error Tracking (uncaught ones get there on their own). */
export function trackError(error, props = {}) {
  ph()?.captureException(error, props);
}

/** Properties attached to every later event (e.g. the current city). */
export function setContext(props) {
  ph()?.register(props);
}

/** For events fired while the page is closing. */
export function trackOnExit(event, props = {}) {
  ph()?.capture(event, props, { transport: 'sendBeacon' });
}

/** Explicitly marked content links only. Beacon delivery survives same-tab navigation. */
export function trackContentLinks(context) {
  for (const a of document.querySelectorAll('a[data-content-destination]')) {
    const report = e => {
      if (e.type === 'auxclick' ? e.button !== 1 : e.button !== 0) return;
      trackOnExit('content_link_clicked', {
        ...context(), destination: a.dataset.contentDestination, placement: a.dataset.contentPlacement,
        ...(a.dataset.contentCity ? { destination_city: a.dataset.contentCity } : {}),
      });
    };
    a.addEventListener('click', report);
    a.addEventListener('auxclick', report);
  }
}
